<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import DocumentStatusBadge from '../DocumentStatusBadge/DocumentStatusBadge.vue';
import { DOCUMENT_TYPE_KEYS } from '../constants';

const props = defineProps({
  document: { type: Object, required: true },
  isTransitioning: { type: Boolean, default: false },
  isUploading: { type: Boolean, default: false },
  isDownloading: { type: Boolean, default: false },
});

const emit = defineEmits(['showDocument', 'transition', 'upload', 'download']);

const RELATED_RECORD_ROUTE_NAMES = {
  contact: 'contacts_edit',
  company: 'companies_dashboard_index',
  deal: 'crm_deal_show',
  task: 'crm_task_show',
  quote: 'crm_quote_show',
};

const { t } = useI18n();

const nextStatuses = computed(() => props.document.nextStatuses || []);
const selectedStatus = ref('');

watch(
  nextStatuses,
  statuses => {
    if (!statuses.includes(selectedStatus.value)) {
      selectedStatus.value = statuses[0] || '';
    }
  },
  { immediate: true }
);

const nextStatusOptions = computed(() =>
  nextStatuses.value.map(status => ({
    value: status,
    label: t(`DOCUMENTS.STATUS.${status.toUpperCase()}`),
  }))
);

const typeLabel = computed(() => {
  const type = props.document.documentType;
  if (!type) return '';
  return DOCUMENT_TYPE_KEYS.includes(type) ? t(`DOCUMENTS.TYPES.${type.toUpperCase()}`) : type;
});

const hasVersion = computed(() => !!props.document.currentVersion);

// Builds a vue-router route object for the related record chip, or null
// when no link is configured. The card-click handler must NOT fire when
// the chip is clicked (handled via @click.stop in the template).
const relatedRecordRoute = computed(() => {
  const kind = props.document.relatedRecordKind;
  const id = props.document.relatedRecordId;
  if (!kind || !id) return null;

  const routeName = RELATED_RECORD_ROUTE_NAMES[kind];
  if (!routeName) return null;

  const paramKey = {
    contact: 'contactId',
    company: 'companyId',
    deal: 'dealId',
    task: 'taskId',
    quote: 'quoteId',
  }[kind];

  return { name: routeName, params: { [paramKey]: id } };
});

// Extra linked records beyond the first one shown by name, e.g. '+2'.
const extraLinksSuffix = computed(() => {
  const extra = (props.document.linksCount || 0) - 1;
  return extra > 0 ? `+${extra}` : '';
});

const formattedDueDate = computed(() => {
  const { dueDate } = props.document;
  if (!dueDate) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(`${dueDate}T00:00:00`));
  } catch {
    return dueDate;
  }
});

const nextStepHint = computed(() => {
  if (nextStatuses.value.length) return '';
  return !hasVersion.value && ['draft', 'requested'].includes(props.document.status)
    ? t('DOCUMENTS.UPLOAD_REQUIRED')
    : t('DOCUMENTS.NO_NEXT_STEP');
});

const onClickCard = () => emit('showDocument', props.document.id);

const onClickMove = () => {
  if (!selectedStatus.value) return;
  emit('transition', { document: props.document, status: selectedStatus.value });
};
</script>

<template>
  <CardLayout
    layout="row"
    class="cursor-pointer transition-all duration-200 hover:outline-n-brand hover:shadow-md group/cardLayout"
    @click="onClickCard"
  >
    <div class="flex items-center justify-start flex-1 gap-4">
      <span
        class="flex items-center justify-center rounded-full size-12 shrink-0 bg-n-slate-3 text-n-slate-11"
      >
        <Icon :icon="hasVersion ? 'i-lucide-file-text' : 'i-lucide-file-clock'" class="size-5" />
      </span>
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
          <span class="inline-flex items-center gap-1.5 min-w-0">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ document.title }}
            </span>
            <Icon
              v-if="document.visibility === 'restricted'"
              icon="i-lucide-lock"
              size="size-3.5 text-n-slate-10 shrink-0"
              :title="$t('DOCUMENTS.ACCESS.VISIBILITY_RESTRICTED')"
            />
          </span>
          <DocumentStatusBadge :status="document.status" />
          <span
            v-if="document.overdue"
            class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-amber-9/10 text-n-amber-11"
          >
            {{ $t('DOCUMENTS.OVERDUE') }}
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
          <span
            v-if="typeLabel"
            class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate"
          >
            {{ typeLabel }}
          </span>
          <template v-if="document.partyName && document.relatedRecordKind !== 'contact'">
            <div class="w-px h-3 bg-n-slate-6" />
            <span class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-user" size="size-3.5 text-n-slate-11" />
              {{ document.partyName }}
            </span>
          </template>
          <template v-if="document.relatedRecordName">
            <div class="w-px h-3 bg-n-slate-6" />
            <router-link
              v-if="relatedRecordRoute"
              :to="relatedRecordRoute"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate hover:text-n-brand-9"
              @click.stop
            >
              <Icon icon="i-lucide-link" size="size-3.5 text-n-slate-11" />
              {{ document.relatedRecordName }}
              <span v-if="extraLinksSuffix" class="text-xs text-n-slate-9 shrink-0">
                {{ extraLinksSuffix }}
              </span>
            </router-link>
            <span v-else class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-link" size="size-3.5 text-n-slate-11" />
              {{ document.relatedRecordName }}
              <span v-if="extraLinksSuffix" class="text-xs text-n-slate-9 shrink-0">
                {{ extraLinksSuffix }}
              </span>
            </span>
          </template>
          <div class="w-px h-3 bg-n-slate-6" />
          <span
            class="inline-flex items-center gap-1.5 text-sm truncate"
            :class="document.overdue ? 'text-n-ruby-11' : 'text-n-slate-11'"
          >
            <Icon icon="i-lucide-calendar" size="size-3.5" />
            {{ formattedDueDate || $t('DOCUMENTS.NO_DUE_DATE') }}
          </span>
          <div class="w-px h-3 bg-n-slate-6" />
          <span class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
            <Icon icon="i-lucide-circle-user" size="size-3.5 text-n-slate-11" />
            {{ document.owner?.name || $t('DOCUMENTS.UNASSIGNED') }}
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-2 mt-3" @click.stop>
          <template v-if="nextStatuses.length">
            <ComboBox v-model="selectedStatus" :options="nextStatusOptions" class="w-44" />
            <Button
              :label="$t('DOCUMENTS.MOVE')"
              size="sm"
              slate
              :is-loading="isTransitioning"
              :disabled="isTransitioning || !selectedStatus"
              @click="onClickMove"
            />
          </template>
          <span v-else class="text-xs text-n-slate-9">{{ nextStepHint }}</span>
          <Button
            v-if="!hasVersion"
            :label="$t('DOCUMENTS.UPLOAD')"
            icon="i-lucide-upload"
            size="sm"
            :is-loading="isUploading"
            :disabled="isUploading"
            @click="emit('upload', document)"
          />
          <Button
            v-else
            icon="i-lucide-upload"
            size="sm"
            ghost
            slate
            :aria-label="$t('DOCUMENTS.REPLACE_FILE')"
            :is-loading="isUploading"
            :disabled="isUploading"
            @click="emit('upload', document)"
          />
          <Button
            v-if="hasVersion"
            icon="i-lucide-download"
            size="sm"
            ghost
            slate
            :aria-label="$t('DOCUMENTS.DOWNLOAD')"
            :is-loading="isDownloading"
            :disabled="isDownloading"
            @click="emit('download', document)"
          />
        </div>
      </div>
    </div>
  </CardLayout>
</template>
